import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward, MdFace, MdStar, MdAutoAwesome, MdOutlineLightMode, MdFilterTiltShift } from 'react-icons/md';

export default function GlowAuraLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#d946ef"; // Pastel purple/fuchsia
  const secondary = T.secondaryColor || "#a855f7"; // Soft violet
  const text = p.storefrontText || {};
  
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [auraMetric, setAuraMetric] = useState("hydration");

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Holistic Aura Glow",
    title: "Light Up Your Skin's Energy.",
    subtitle: "Empower your self-care routine with holographic skincare infusions designed to elevate your skin's natural frequency.",
    primaryCta: "Find Your Glow",
    secondaryCta: "Aura Test",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafafc] text-[#1e1b4b] antialiased" style={{ fontFamily: T.fontFamily || "Outfit, sans-serif" }}>
      {/* Iridescent banner message */}
      <div className="w-full bg-gradient-to-r from-fuchsia-300 via-purple-300 to-indigo-300 py-2.5 px-4 text-center text-xs font-semibold tracking-wide text-indigo-950 shadow-inner">
        <span>✨ GET THE AURIC GLOW: Spend $50 and receive a free Amethyst Facial Roller! </span>
        <button onClick={(e) => navigateTo("shop", e)} className="underline hover:text-black ml-1 inline-flex items-center gap-0.5">
          Claim gift <MdArrowForward className="text-[10px]" />
        </button>
      </div>

      {/* Glassmorphic Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#fafafc]/80 backdrop-blur-md border-b border-purple-100 shadow-sm" 
          : "bg-transparent border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-fuchsia-200 shadow-[0_0_12px_rgba(217,70,239,0.15)]" size="h-9 w-9" />
            <div>
              <p className="text-base font-extrabold tracking-tight bg-gradient-to-r from-fuchsia-600 to-indigo-600 bg-clip-text text-transparent">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-[#a855f7] tracking-widest font-extrabold uppercase mt-0.5">{p.storeInfo?.businessCategory || "Aura Beauty"}</p>
            </div>
          </div>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Shop Aura"],
              ["details", "About"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-bold tracking-wider uppercase transition-colors hover:text-[#d946ef] ${
                  activePage === page 
                    ? "text-[#d946ef]" 
                    : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search, Wishlist, Cart */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-48" />
            
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-xl hover:bg-fuchsia-50 text-[#1e1b4b] transition-colors" title="Wishlist">
              <FaHeart className="text-base text-fuchsia-500" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-fuchsia-600 shadow-sm">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />
            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-xl text-[#1e1b4b]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#fafafc] border-b border-purple-100 shadow-2xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Shop Aura"],
              ["details", "About Us"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-xs font-bold tracking-wider uppercase border-b border-purple-50 hover:text-[#d946ef] ${
                  activePage === page ? "text-[#d946ef] font-bold" : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Holographic Hero Box */}
            <section className="max-w-6xl mx-auto px-6 pt-6 pb-12">
              <div className="relative rounded-3xl overflow-hidden bg-white border border-purple-50 shadow-[0_20px_50px_rgba(217,70,239,0.06)] p-8 md:p-14 flex flex-col md:flex-row items-center gap-10 min-h-[460px]">
                {/* Iridescent background blur elements */}
                <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full bg-fuchsia-300/30 blur-[110px] pointer-events-none" />
                <div className="absolute -bottom-32 -right-32 w-80 h-80 rounded-full bg-indigo-300/35 blur-[110px] pointer-events-none" />

                <div className="flex-1 text-left relative z-10">
                  <span className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-widest text-[#a855f7] bg-purple-50 px-3.5 py-1.5 rounded-full border border-purple-100 mb-4">
                    <MdAutoAwesome className="text-sm text-fuchsia-500 animate-spin-slow" />
                    <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                  </span>
                  <h1 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight mb-4 tracking-tight">
                    <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                  </h1>
                  <p className="text-slate-400 text-sm leading-relaxed mb-8 max-w-lg">
                    <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                  </p>
                  
                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => navigateTo("shop")}
                      className="px-8 py-3.5 rounded-full text-xs font-bold uppercase tracking-wider text-white shadow-lg hover:shadow-fuchsia-500/10 active:scale-98 transition-all"
                      style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
                    >
                      <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                    </button>
                    <button 
                      onClick={() => { p.setActiveTab("details"); navigateTo("about"); }}
                      className="px-8 py-3.5 rounded-full text-xs font-bold uppercase tracking-wider text-slate-700 border border-purple-100 bg-transparent hover:bg-fuchsia-50/30 transition-colors"
                    >
                      <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                    </button>
                  </div>
                </div>

                <div className="flex-1 w-full relative z-10 self-stretch rounded-2xl overflow-hidden border border-purple-50 shadow-inner bg-purple-50/20">
                  <HeroImage p={p} themeId="glowaura" className="h-[280px] md:h-full">
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-white/30 to-transparent pointer-events-none" />
                  </HeroImage>
                </div>
              </div>
            </section>

            {/* Auric Skin energy levels spotlight */}
            <section className="max-w-6xl mx-auto px-6 py-12">
              <div className="bg-[#fafaff] border border-purple-100 rounded-3xl p-6 md:p-12">
                <div className="max-w-xl text-left mb-8">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#a855f7]">SKIN ENERGY SPECTRUM</span>
                  <h2 className="text-3xl font-extrabold text-slate-900 mt-1">Light infusion technology.</h2>
                  <p className="text-slate-500 text-xs mt-2">Adjust your daily routine based on your skin's vital levels. Luxe formulas targeted at structural skin repair.</p>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-stretch">
                  <div className="flex md:flex-col gap-2 overflow-x-auto pb-3 md:pb-0 border-b md:border-b-0 md:border-r border-purple-100 w-full md:w-56 shrink-0 text-left">
                    {[
                      { id: "hydration", label: "Dewy Hydration", icon: <MdOutlineLightMode /> },
                      { id: "cellular", label: "Cellular Energy", icon: <MdFilterTiltShift /> },
                      { id: "barrier", label: "Barrier Resilience", icon: <MdFace /> }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setAuraMetric(tab.id)}
                        className={`px-4 py-3 rounded-full text-xs font-bold flex items-center gap-3 transition-colors ${
                          auraMetric === tab.id 
                            ? "bg-purple-600/10 text-[#a855f7] border-l-2 border-[#a855f7]" 
                            : "text-slate-400 hover:text-slate-700"
                        }`}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex-1 text-left bg-white border border-purple-50 p-6 md:p-8 rounded-2xl flex flex-col justify-between">
                    {auraMetric === "hydration" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-base font-bold text-slate-800">Moisture-Lock Hyaluronic Compound</h4>
                        <p className="text-slate-400 text-xs mt-2 leading-relaxed">Formulated with custom micro-holographic peptides that expand inside the epidermis layer to seal in moisture and provide a visible dewy bounce.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#fdfdfd] border border-purple-50 p-4 rounded-xl">
                            <span className="text-2xl font-black text-fuchsia-600">82%</span>
                            <p className="text-[10px] text-slate-500 mt-1">Hydration Index Boost</p>
                          </div>
                          <div className="bg-[#fdfdfd] border border-purple-50 p-4 rounded-xl">
                            <span className="text-2xl font-black text-fuchsia-600">3x</span>
                            <p className="text-[10px] text-slate-500 mt-1">Faster Absorbing Rate</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {auraMetric === "cellular" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-base font-bold text-slate-800">ATP Mitochondrial Recharge</h4>
                        <p className="text-slate-400 text-xs mt-2 leading-relaxed">Revitalizes tired cells. Infused with stabilized Coenzyme Q10 and Niacinamide to accelerate cellular cycle turn rates and counter free radical damage.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#fdfdfd] border border-purple-50 p-4 rounded-xl">
                            <span className="text-2xl font-black text-fuchsia-600">14 Days</span>
                            <p className="text-[10px] text-slate-500 mt-1">Cell Cycle Turnaround</p>
                          </div>
                          <div className="bg-[#fdfdfd] border border-purple-50 p-4 rounded-xl">
                            <span className="text-2xl font-black text-fuchsia-600">+45%</span>
                            <p className="text-[10px] text-slate-500 mt-1">Skin Firmness Index</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {auraMetric === "barrier" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-base font-bold text-slate-800">Multi-Ceramide Skin Cushion</h4>
                        <p className="text-slate-400 text-xs mt-2 leading-relaxed">Combines five skin-identical ceramides to repair and seal micro-cracks in the outer protective layer. Prevents moisture loss and soothes skin flare-ups.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#fdfdfd] border border-purple-50 p-4 rounded-xl">
                            <span className="text-2xl font-black text-fuchsia-600">5 Types</span>
                            <p className="text-[10px] text-slate-500 mt-1">Skin Ceramides</p>
                          </div>
                          <div className="bg-[#fdfdfd] border border-purple-50 p-4 rounded-xl">
                            <span className="text-2xl font-black text-fuchsia-600">100%</span>
                            <p className="text-[10px] text-slate-500 mt-1">Safe Sensitive Guard</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Glowing Pastels Badges */}
            <section className="bg-white border-y border-purple-50 py-10 px-6">
              <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "Cruelty-Free Approved", icon: <MdStar className="text-2xl text-fuchsia-500" />, desc: "Certified vegan & clean" },
                  { title: "Dermatological Base", icon: <MdAutoAwesome className="text-2xl text-fuchsia-500" />, desc: "Lab approved safe formulas" },
                  { title: "Recycled Packaging", icon: <MdFilterTiltShift className="text-2xl text-fuchsia-500" />, desc: "100% sustainable materials" },
                  { title: "Auric Guarantee", icon: <MdFace className="text-2xl text-fuchsia-500" />, desc: "30-day glow satisfaction check" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-5 border border-purple-50 rounded-2xl bg-[#fdfdfd] hover:shadow-md transition-shadow">
                    <div className="p-3.5 rounded-full bg-purple-50 mb-3 border border-purple-100">{b.icon}</div>
                    <p className="text-xs font-bold text-slate-900">{b.title}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-[#a855f7]">CATALOG</span>
              <h3 className="text-3xl font-extrabold text-slate-900 mt-1">Light Infused Products</h3>
            </div>

            {/* Filter and Tab Panel Toggle */}
            <div className="flex items-center gap-2.5">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-purple-100 bg-white hover:bg-slate-50 text-xs font-bold text-slate-700 transition-colors"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-full border border-purple-100 bg-white p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: primary, color: "#fff" } : { color: "#64748b" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} />
    </div>
  );
}
